import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { ScrollFade, StaggerGroup, StaggerItem, HeroStaggerText } from "@/lib/animations";

// Curated imagery per category — easy to swap with real photos later
const CATEGORY_IMAGES = {
  "vegan-pu-leather": "https://images.unsplash.com/photo-1521223890158-f9f7c3d5d504?w=1000&q=80",
  "vegan-pu-fur-leather": "https://images.unsplash.com/photo-1578198576866-7e0ba6078128?w=1000&q=80",
  "suede": "https://images.pexels.com/photos/7026422/pexels-photo-7026422.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "corduroy": "https://images.pexels.com/photos/7904113/pexels-photo-7904113.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "cotton": "https://images.pexels.com/photos/15396619/pexels-photo-15396619.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "puffer": "https://images.unsplash.com/photo-1614252369475-531eba835eb1?w=1000&q=80",
  "polyester": "https://images.unsplash.com/photo-1619102814948-e164e584cf0c?w=1000&q=80",
  "windcheater": "https://images.unsplash.com/photo-1619603364937-8d7af41ef206?w=1000&q=80",
};

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    api.get("/categories").then(r => setCats(r.data || []));
    api.get("/products?limit=500").then(r => {
      const map = {};
      (r.data || []).forEach(p => { map[p.category] = (map[p.category] || 0) + 1; });
      setCounts(map);
    });
  }, []);

  return (
    <Layout>
      <section className="bg-[#163A2F] text-[#F8F6F0] grain relative pt-20 pb-24 md:pt-28 md:pb-32">
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="overline text-[#C9A227]">Browse</div>
          <HeroStaggerText
            text="Categories."
            className="mt-3 text-5xl md:text-7xl font-extralight tracking-tight"
          />
          <p className="mt-5 max-w-xl text-[#F8F6F0]/75 font-light">
            Nine premium fabrications, one obsession with fit and finish. Explore the full Norbu universe of men's jackets.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F6F0] py-20 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {cats.map((c, i) => (
              <StaggerItem key={c.slug}>
                <Link
                  to={`/category/${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className="group block relative overflow-hidden aspect-[4/5] bg-[#163A2F]/[0.04]"
                >
                  <img
                    src={CATEGORY_IMAGES[c.slug]}
                    alt={c.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-[1.07]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C16]/85 via-[#0A1C16]/25 to-transparent" />
                  <div className="absolute top-5 left-5 right-5 flex justify-between items-start text-[#F8F6F0]/85">
                    <span className="overline text-[#C9A227]">0{i + 1}</span>
                    <span className="overline text-[#F8F6F0]/70">{counts[c.slug] || 0} pieces</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7 text-[#F8F6F0]">
                    <div className="flex items-end justify-between gap-4">
                      <div>
                        <h3 className="text-2xl md:text-3xl font-light tracking-tight leading-tight">{c.name}</h3>
                        <p className="mt-1 text-sm text-[#F8F6F0]/70 font-light max-w-xs">{c.tagline}</p>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-[#C9A227] -translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <ScrollFade delay={0.1} className="mt-16 text-center">
            <Link to="/shop" className="border border-[#163A2F] text-[#163A2F] hover:bg-[#163A2F] hover:text-white transition-colors px-10 py-4 uppercase text-xs tracking-[0.25em] inline-block">
              View all products
            </Link>
          </ScrollFade>
        </div>
      </section>
    </Layout>
  );
}
